import { useEffect, useRef } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import {
  PlaceOutlined as WaarIcon,
  GroupsOutlined as WieIcon,
  ScheduleOutlined as PlanningIcon,
  StickyNote2Outlined as OpmerkingenIcon,
  Inventory2Outlined as OpleveringIcon,
  VideocamOutlined as BriefingIcon,
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import { mdLinkComponents } from '../../utils/briefingBronnen';
import { telefoonNl } from '../../utils/telefoon';

/**
 * Bouwstenen voor de callsheetpagina in documentvorm (Joris 28 sep: "best rommelig").
 *
 * De pagina volgt de PDF die de crew krijgt: één kop (titel, datum, project, klaar-regel),
 * daarna secties met een rustig label/waarde-raster. Elke waarde staat op één plek en wordt
 * op één manier bewerkt: klik op de waarde → veld; Enter of klik-buiten bewaart, Esc annuleert.
 */

export const ORANJE = '#b45309';
export const GRIJS = '#64748b';
export const LIJN = '#eef0f3';
// Leesbaarheid (Joris 29 sep, live op TIVO): waarden 15px, labels 13px, regelhoogte 1.55.
export const WAARDE_PX = 15;
export const LABEL_PX = 13;
export const REGELHOOGTE = 1.55;
export const INKT = '#0f172a';

// ── Hiërarchie en kleur (team via Joris, 7 okt: "de hiërarchie is verdwenen, er mag iets meer kleur in") ──
// Kleur structureert, het versiert niet: elke sectie heeft één eigen accent (kleur van de
// sectiekop-tegel en, bij 'Wanneer & waar', een lichte achtergrond). De tinten komen uit de
// groep-accenten van de hubs (HubPage DEFAULT_GROUP_ACCENT) en de ff-design-tokens:
// Pre-productie blauw, Resources groen, tertiary paars, primary leisteen. Oranje blijft
// gereserveerd voor 'ontbreekt'.
export const SECTIE_ACCENT = {
  waar: { kleur: '#1d4ed8', zacht: '#eff6ff', rand: '#dbeafe', Icoon: WaarIcon },
  wie: { kleur: '#047857', zacht: '#ecfdf5', rand: '#d1fae5', Icoon: WieIcon },
  planning: { kleur: '#635983', zacht: '#f3f0fb', rand: '#e4ddf7', Icoon: PlanningIcon },
  opmerkingen: { kleur: '#526075', zacht: '#eef2f9', rand: '#d5e3fd', Icoon: OpmerkingenIcon },
  oplevering: { kleur: '#526075', zacht: '#eef2f9', rand: '#d5e3fd', Icoon: OpleveringIcon },
  briefing: { kleur: '#635983', zacht: '#f3f0fb', rand: '#e4ddf7', Icoon: BriefingIcon },
};

// Sectievolgorde = de volgorde van de PDF (Joris, 14 okt: "PDF is leidend"): settelefoon/contact,
// deliverables, opmerkingen + productievelden (kleding), locaties, crew, cast, planning. Begin- en
// eindtijd staan bovenaan bij het contact (PDF-kop: datum); de briefing is crew-only en staat
// niet in de PDF, dus als apart blok onderaan. Engine-PDF (callsheets_pdf.py), Node-PDF
// (routes/callsheets.js) en het Word-bestand (callsheet_docx.py) volgen dezelfde volgorde.
export const SECTIE_TITEL = {
  wanneer: 'Wanneer & contact',
  oplevering: 'Oplevering',
  opmerkingen: 'Opmerkingen',
  waar: 'Waar',
  wie: 'Wie',
  planning: 'Planning',
  briefing: 'Briefing crew',
};
export const SECTIE_VOLGORDE = ['wanneer', 'oplevering', 'opmerkingen', 'waar', 'wie', 'planning', 'briefing'];

// Crew: functie als kleurchip per discipline, zodat je in één oogopslag ziet wie camera, licht,
// geluid, regie/productie of visagie/styling doet. Volgorde van de regels blijft die van de PL
// (dezelfde volgorde als de PDF en de crew-app).
const ROL_KLEUREN = [
  // Specifiek vóór algemeen: 'Director of Photography' is camera, niet regie (Codex r1).
  { sleutel: 'camera', test: /camera|\bdop\b|d\.o\.p|director of photography|cinematograa?f|focus ?puller|drone|fotogra(af|pher)|video ?gra(af|ph)|\bdit\b/i, kleur: '#1d4ed8', zacht: '#dbeafe' },
  { sleutel: 'regie', test: /\bregie|\bregisseu|producer|productie|projectleid|\bpl\b|\bdirector\b/i, kleur: '#5b21b6', zacht: '#ede9fe' },
  { sleutel: 'licht', test: /licht|gaffer|best boy|grip|elektr/i, kleur: '#92400e', zacht: '#fef3c7' },
  { sleutel: 'geluid', test: /geluid|sound|audio|boom/i, kleur: '#0f766e', zacht: '#ccfbf1' },
  { sleutel: 'visagie', test: /visag|make-?up|haar|hair|styl|kled|wardrobe|kostuum/i, kleur: '#9d174d', zacht: '#fce7f3' },
];
const ROL_OVERIG = { sleutel: 'overig', kleur: '#475569', zacht: '#f1f5f9' };
export const rolKleur = (functie) => {
  const f = String(functie || '');
  return (f && ROL_KLEUREN.find((r) => r.test.test(f))) || ROL_OVERIG;
};

/** Functie als zachte kleurchip (alleen weergave). */
export function RolChip({ functie }) {
  const tekst = String(functie || '').trim();
  if (!tekst) return null;
  const k = rolKleur(tekst);
  return (
    <Box component="span" data-testid="rol-chip" data-rol={k.sleutel}
      sx={{ display: 'inline-block', px: 1, py: '1px', borderRadius: 10, bgcolor: k.zacht, color: k.kleur,
        fontSize: 12.5, fontWeight: 600, lineHeight: 1.6, whiteSpace: 'normal', maxWidth: '100%', overflowWrap: 'anywhere',
        '@media print': { bgcolor: 'transparent', px: 0 } }}>
      {tekst}
    </Box>
  );
}

// Tijden scanbaar: vaste breedte cijfers, vet, in de sectiekleur.
export const tijdSx = (kleur = INKT) => ({ fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: kleur });

// ── Tekst met structuur (alleen weergave; de opgeslagen tekst blijft zoals hij is) ──
// Blokken worden gescheiden door een lege regel. Binnen een blok wordt elke reeks van twee of meer
// losse regels (geen lijst, kop, citaat of ingesprongen vervolg) een opsomming, ook onder een kop;
// '• ' wordt een markdown-bullet; een enkele losse regel blijft een alinea. Een bullet die met een
// nummer begint ("- 01. Aftermovie") blijft één bullet in plaats van een geneste genummerde lijst.
// Zo leest "Uitloop tot 18:00\nParkeren achter\nLunch om 12:30" als drie bullets.
const MD_BLOK = /^\s*([-*+•]\s|\d+[.)]\s|#{1,6}\s|>|\|)/;
const BULLET = /^(\s*)[-*+]\s+/;
const naarBullet = (r) => r.replace(/^(\s*)•\s*/, '$1- ');
// "- 01. Aftermovie" → "- 01\. Aftermovie": het nummer is tekst, geen nieuwe lijst.
const nummerAlsTekst = (r) => r.replace(/^(\s*[-*+]\s+)(\d+)([.)])(\s)/, '$1$2\\$3$4');
export function losseRegelsAlsLijst(tekst) {
  const blokken = String(tekst || '').replace(/\r\n?/g, '\n').split(/\n\s*\n/);
  return blokken.map((blok) => {
    const regels = blok.split('\n').map((r) => naarBullet(r.replace(/\s+$/, ''))).filter((r) => r.trim());
    const los = (r) => !MD_BLOK.test(r) && !/^\s/.test(r);
    const uit = [];
    for (let i = 0; i < regels.length;) {
      if (!los(regels[i])) { uit.push(regels[i]); i += 1; continue; }
      let j = i;
      while (j < regels.length && los(regels[j])) j += 1;
      const reeks = regels.slice(i, j);
      if (reeks.length >= 2) uit.push(...reeks.map((r) => `- ${r.trim()}`));
      else uit.push(reeks[0]);
      i = j;
    }
    return uit.map((r) => (BULLET.test(r) ? nummerAlsTekst(r) : r)).join('\n');
  }).filter(Boolean).join('\n\n');
}

export const tekstOpmaakSx = {
  fontSize: WAARDE_PX, lineHeight: REGELHOOGTE, color: INKT, whiteSpace: 'normal',
  '& p': { m: 0, fontSize: 'inherit', lineHeight: 'inherit' },
  '& p + p, & p + ul, & ul + p, & p + ol, & ol + p, & ul + ul': { mt: 1 },
  '& ul, & ol': { m: 0, pl: 2.5 },
  '& li': { mb: 0.25, pl: 0.25 },
  '& li::marker': { color: '#94a3b8' },
  '& h1, & h2, & h3, & h4': { fontSize: WAARDE_PX, fontWeight: 700, m: 0, mt: 1.5, mb: 0.5 },
  '& > :first-of-type': { mt: 0 },
  '& a': { color: '#4338ca', wordBreak: 'break-all' },
};

/** Lange tekst met structuur: bullets, alinea's, koppen. */
export function TekstOpmaak({ tekst, components = mdLinkComponents }) {
  return (
    <Box data-testid="tekst-opmaak" sx={tekstOpmaakSx}>
      <ReactMarkdown components={components}>{losseRegelsAlsLijst(tekst)}</ReactMarkdown>
    </Box>
  );
}

// ── Klaar voor versturen ────────────────────────────────────────────────────────
// Wat een crewlid op de dag echt nodig heeft. Volgorde = volgorde op de pagina.
export const KLAAR_PUNTEN = [
  { key: 'begintijd', label: 'begintijd' },
  { key: 'eindtijd', label: 'eindtijd' },
  { key: 'settelefoon', label: 'settelefoon' },
  { key: 'contact', label: 'contact klant' },
  { key: 'telefoon', label: 'telefoon klant' },
  { key: 'locatie', label: 'locatie' },
  { key: 'crew', label: 'crew' },
];

// Contact en telefoon klant (Codex r2). In Airtable is 'Contactpersoon' / 'Telefoonnummer klant' een
// formule: het handmatige veld, anders de contactpersoon uit het project. De PDF gebruikt
// handmatig || formule. Daarom maakt de pagina de herkomst zichtbaar:
//   handmatig  → eigen waarde op deze callsheet
//   project    → handmatig leeg, de waarde komt uit het project ("uit project: …")
//   geen       → bewust geen contact op deze callsheet: '-' in het handmatige veld (bestaand veld,
//                geen schemawijziging; de formule en de PDF tonen dan '-', het lege teken)
//   leeg       → niets bekend: ontbreekt
export const GEEN_CONTACT = '-';
export const CONTACT_VELDEN = {
  naam: { handmatig: 'Contactpersoon (handmatig)', formule: 'Contactpersoon' },
  telefoon: { handmatig: 'Contactgegevens klant telefoon (Handmatig)', formule: 'Telefoonnummer klant' },
};
const alsTekst = (v) => (Array.isArray(v) ? v.filter(Boolean).join(', ') : String(v ?? '')).trim();

export function contactStatus(f = {}, soort = 'naam') {
  const { handmatig, formule } = CONTACT_VELDEN[soort];
  const hand = alsTekst(f[handmatig]);
  if (hand === GEEN_CONTACT) return { soort: 'geen', waarde: '', handmatig: '' };
  if (hand) return { soort: 'handmatig', waarde: hand, handmatig: hand };
  const project = alsTekst(f[formule]);
  if (project && project !== GEEN_CONTACT) return { soort: 'project', waarde: project, handmatig: '' };
  return { soort: 'leeg', waarde: '', handmatig: '' };
}
export const contactNaamVan = (f = {}) => contactStatus(f, 'naam').waarde;
export const contactTelVan = (f = {}) => contactStatus(f, 'telefoon').waarde;
// ── Telefoon en namen (zelfde regels als de PDF, backend/utils/callsheetTekst.js) ──
/** Eén notatie: '06 12 34 56 78' / '010 123 4567'; +31/0031 → 0; buitenland blijft (utils/telefoon.js). */
export const formatTelefoon = telefoonNl;
/** "Paul Caspers (inFrame)" → "Paul Caspers": het bedrijf uit Personen eraf. */
export const stripBedrijf = (naam) => String(naam ?? '').replace(/(\s*\([^()]*\))+\s*$/, '').trim();

/** Bestandsnaam uit Content-Disposition: filename* (UTF-8) gaat voor filename. */
export function bestandsnaamUitHeader(header) {
  const h = String(header || '');
  const ster = h.match(/filename\*\s*=\s*(?:UTF-8|utf-8)''([^;]+)/);
  if (ster) { try { return decodeURIComponent(ster[1].trim()); } catch { /* val terug */ } }
  const gewoon = h.match(/filename\s*=\s*"([^"]*)"|filename\s*=\s*([^;]+)/);
  return (gewoon && (gewoon[1] ?? gewoon[2] ?? '').trim()) || '';
}

export const settelefoonVan = (f = {}) => ({
  naam: f['Naam (from Contactpersoon Settelefoon)']?.[0] || '',
  nummer: f['FullFrame Settelefoonnummer']?.[0] || '',
});

/** Welke punten van KLAAR_PUNTEN ontbreken nog? Geeft de punten zelf terug (key + label). */
export function bepaalOntbrekend({ fields = {}, extra = {}, locaties = [], crew = [] }) {
  const st = settelefoonVan(fields);
  const leeg = {
    begintijd: !String(extra.begintijd || '').trim(),
    eindtijd: !String(extra.eindtijd || '').trim(),
    locatie: !(locaties || []).length,
    // 'geen' is een bewuste keuze (geen contact op deze callsheet) en telt dus niet als open.
    contact: contactStatus(fields, 'naam').soort === 'leeg',
    telefoon: contactStatus(fields, 'telefoon').soort === 'leeg',
    settelefoon: !st.nummer,   // de crew heeft een nummer nodig, een naam alleen is niet genoeg
    crew: !(crew || []).length,
  };
  return KLAAR_PUNTEN.filter((p) => leeg[p.key]);
}

/** "Klaar voor versturen: 2 open (telefoon klant, settelefoon)" — elk punt springt naar zijn veld. */
export function KlaarRegel({ ontbrekend, onSpring }) {
  if (!ontbrekend.length) {
    return (
      <Typography component="span" variant="body2" data-testid="klaar-regel" sx={{ color: GRIJS }}>
        Klaar voor versturen ✓
      </Typography>
    );
  }
  return (
    <Typography component="span" variant="body2" data-testid="klaar-regel" sx={{ color: ORANJE }}>
      Klaar voor versturen: {ontbrekend.length} open (
      {ontbrekend.map((p, i) => (
        <span key={p.key}>
          {i > 0 && ', '}
          <Box component="button" type="button" onClick={() => onSpring(p.key)} data-testid={`ontbreekt-${p.key}`}
            sx={{ all: 'unset', cursor: 'pointer', color: ORANJE, fontWeight: 600, textDecoration: 'underline',
              textDecorationStyle: 'dotted', textUnderlineOffset: 3, '&:focus-visible': { outline: `2px solid ${ORANJE}` } }}>
            {p.label}
          </Box>
        </span>
      ))}
      )
    </Typography>
  );
}

// ── Oplevering ──────────────────────────────────────────────────────────────────
// Deliverables staan (sinds from-draaidag) als "📦 Deliverables:"-blok in Algemene opmerkingen.
// De PDF licht dat blok eruit (backend/routes/callsheets.js splitDeliverableBlock); de pagina doet
// hetzelfde, zodat opmerkingen en oplevering elk op hun eigen plek staan.
const BLOK_KOP = /^📦\s*Deliverables/i;

export function splitsOplevering(tekst) {
  const out = [];
  const oplevering = [];
  let inBlok = false;
  for (const regel of String(tekst || '').split('\n')) {
    if (BLOK_KOP.test(regel.trim())) { inBlok = true; continue; }
    if (inBlok) {
      if (/^[-•]/.test(regel.trim())) { oplevering.push(regel.trim().replace(/^[-•]\s*/, '')); continue; }
      inBlok = false;
      if (regel.trim() === '') continue;
    }
    out.push(regel);
  }
  return { opmerkingen: out.join('\n').trim(), oplevering };
}

export function voegOpleveringSamen(opmerkingen, oplevering = []) {
  const regels = (oplevering || []).map((r) => String(r).trim().replace(/^[-•]\s*/, '')).filter(Boolean);
  const blok = regels.length ? `📦 Deliverables:\n${regels.map((r) => `- ${r}`).join('\n')}` : '';
  return [String(opmerkingen || '').trim(), blok].filter(Boolean).join('\n\n');
}

// ── Opmaak ──────────────────────────────────────────────────────────────────────
export function Sectie({ id, titel, acties, children, accent, uitgelicht = false }) {
  const a = accent || SECTIE_ACCENT.opmerkingen;
  const { Icoon } = a;
  return (
    <Box component="section" id={id} data-testid={id} aria-label={titel}
      sx={{
        py: 2.75, borderTop: `1px solid ${LIJN}`, '&:first-of-type': { borderTop: 0, pt: 0.5 },
        // 'Wanneer & waar' is het belangrijkste blok: lichte tint in de sectiekleur.
        ...(uitgelicht ? {
          bgcolor: a.zacht, border: `1px solid ${a.rand}`, borderRadius: 2, px: { xs: 1.5, md: 2.25 }, py: 2,
          mb: 1, '&:first-of-type': { pt: 2 },
          '& + section': { borderTop: 0, pt: 1.5 },
          '@media print': { bgcolor: 'transparent' },
        } : {}),
      }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5, flexWrap: 'wrap' }}>
        {Icoon && (
          <Box aria-hidden sx={{ width: 28, height: 28, borderRadius: 1.25, display: 'grid', placeItems: 'center', flexShrink: 0,
            bgcolor: uitgelicht ? '#fff' : a.zacht, color: a.kleur }}>
            <Icoon sx={{ fontSize: 17 }} />
          </Box>
        )}
        <Typography component="h2" sx={{ fontSize: 17, fontWeight: 800, letterSpacing: '-0.01em', color: INKT, lineHeight: 1.3 }}>
          {titel}
        </Typography>
        {acties && <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>{acties}</Box>}
      </Box>
      {children}
    </Box>
  );
}

export function Rij({ label, children, id }) {
  return (
    <Box id={id} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '150px 1fr' }, columnGap: 2, rowGap: 0.25, py: 0.75, alignItems: 'baseline' }}>
      <Typography variant="body2" className="rij-label" sx={{ color: GRIJS, fontSize: LABEL_PX, lineHeight: REGELHOOGTE }}>{label}</Typography>
      <Box sx={{ minWidth: 0 }}>{children}</Box>
    </Box>
  );
}

export const tekstKnopSx = { textTransform: 'none', minWidth: 0, px: 1, color: '#334155', fontWeight: 500 };

// ── Klik → veld ─────────────────────────────────────────────────────────────────
/**
 * Eén manier van bewerken. `ctx` = { editingField, setEditingField, editValue, setEditValue }
 * (gedeeld met de pagina, zodat de klaar-regel een veld kan openen). `veld` is de sleutel.
 *
 * soort: 'tekst' | 'lang' (meerdere regels; Enter = nieuwe regel, Ctrl/⌘+Enter of klik-buiten bewaart)
 *        | 'datum' | 'tijd'. Met `editor` render je een eigen veld (bv. het contactzoekveld);
 *        die krijgt { sluit } mee.
 */
export function KlikVeld({ veld, waarde, weergave, onBewaar, ctx, soort = 'tekst', leeg = '—', ontbreekt = false,
  editor, groot = false, label, hulp, bewerkWaarde, origineel, titelRegels, waardeSx }) {
  // bewerkWaarde: waarmee het veld opent als dat afwijkt van wat er staat (bv. leeg bij een projectcontact).
  const start = bewerkWaarde !== undefined ? bewerkWaarde : waarde;
  const { editingField, setEditingField, editValue, setEditValue } = ctx;
  const skipBlur = useRef(false);
  const bewerken = editingField === veld;
  // Elke bewerksessie begint schoon: na Enter/Esc bleef de vlag anders staan en negeerde de
  // volgende sessie de blur — dan was die wijziging kwijt (Codex r1). Ook de klaar-regel opent
  // velden (zonder open()), vandaar het effect.
  useEffect(() => { if (bewerken) skipBlur.current = false; }, [bewerken]);
  const sluit = () => setEditingField(null);

  if (bewerken && editor) return <Box data-veld={veld}>{editor({ sluit })}</Box>;

  if (bewerken) {
    const lang = soort === 'lang';
    const commit = () => {
      sluit();
      // origineel: wat er echt staat als dat afwijkt van de startwaarde (bv. '-' = bewust leeg).
      if (String(editValue ?? '') !== String((origineel !== undefined ? origineel : start) ?? '')) onBewaar(editValue);
    };
    return (
      <Box data-veld={veld}>
        <TextField
          value={editValue ?? ''}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={() => { if (skipBlur.current) { skipBlur.current = false; return; } commit(); }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') { e.preventDefault(); skipBlur.current = true; sluit(); return; }
            if (e.key === 'Enter' && (!lang || e.metaKey || e.ctrlKey) && !e.shiftKey) {
              e.preventDefault(); skipBlur.current = true; commit();
            }
          }}
          autoFocus
          fullWidth={soort === 'tekst' || lang}
          size="small"
          variant="standard"
          multiline={lang || soort === 'tekst'}
          minRows={lang ? 3 : 1}
          type={soort === 'datum' ? 'date' : soort === 'tijd' ? 'time' : undefined}
          inputProps={{ 'aria-label': label || veld, dir: 'ltr', style: groot ? { fontSize: 26, fontWeight: 800 } : { fontSize: WAARDE_PX, lineHeight: REGELHOOGTE } }}
          helperText={hulp || (lang ? 'Klik buiten het veld of Ctrl+Enter om op te slaan · Esc annuleert' : 'Enter bewaart · Esc annuleert')}
        />
      </Box>
    );
  }

  const heeft = waarde != null && String(waarde).trim() !== '';
  const open = () => { skipBlur.current = false; setEditValue(start ?? ''); setEditingField(veld); };
  const lang = soort === 'lang';
  // Lange teksten krijgen structuur (bullets/alinea's); alleen weergave.
  const inhoud = weergave ?? (lang && typeof waarde === 'string' ? <TekstOpmaak tekst={waarde} /> : waarde);
  // Titel: hoogstens `titelRegels` regels, de volledige tekst in de tooltip (title-attribuut).
  const afkap = titelRegels ? { display: '-webkit-box', WebkitLineClamp: titelRegels, WebkitBoxOrient: 'vertical', overflow: 'hidden' } : {};
  return (
    <Box role="button" tabIndex={0} data-veld={veld} aria-label={label ? `${label} bewerken` : undefined}
      title={titelRegels && heeft ? `${waarde} — klik om te bewerken` : 'Klik om te bewerken'}
      onClick={(e) => { if (e.target.closest?.('a')) return; open(); }}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }}
      sx={{ cursor: 'text', borderRadius: 0.75, mx: -0.75, px: 0.75, py: 0.1, display: groot || (lang && heeft) ? 'block' : 'inline-block',
        minWidth: 40, maxWidth: '100%', whiteSpace: lang ? 'normal' : 'pre-wrap', wordBreak: 'break-word',
        '&:hover, &:focus-visible': { bgcolor: 'rgba(15,23,42,0.05)', outline: 'none' } }}>
      {heeft ? (
        <Typography component={lang && weergave === undefined ? 'div' : 'span'} variant={groot ? 'h5' : 'body2'}
          className={waardeSx ? 'cs-nadruk' : undefined}
          sx={groot ? { fontWeight: 800, color: '#1f2937', lineHeight: 1.3, ...afkap } : { color: INKT, fontSize: WAARDE_PX, lineHeight: REGELHOOGTE, ...waardeSx }}>
          {inhoud}
        </Typography>
      ) : (
        <Typography component="span" variant={groot ? 'h5' : 'body2'} sx={{ color: ontbreekt ? ORANJE : '#94a3b8', fontWeight: ontbreekt ? 600 : 400, ...(groot ? {} : { fontSize: WAARDE_PX, lineHeight: REGELHOOGTE }) }}>
          {ontbreekt ? 'ontbreekt' : leeg}
        </Typography>
      )}
    </Box>
  );
}

// ── Planning: invoegen en verslepen zonder de rest te verschuiven (Joris 29 sep) ──────
// Tijden in seconden sinds middernacht; duur in minuten. Andere regels veranderen nooit;
// alleen "Herbereken tijden" zet alles achter elkaar.
export const STANDAARD_DUUR_MIN = 30;
export const sorteerPlanning = (planning) => [...(planning || [])].sort((a, b) => (a.fields?.Tijd || 0) - (b.fields?.Tijd || 0));
export const eindeVan = (p) => (p?.fields?.Tijd || 0) + (Number(p?.fields?.['Duur (minuten)']) || 0) * 60;

/** Begintijd voor een nieuwe regel op positie `index` (0 = boven de eerste, length = achteraan). */
export function tijdVoorInvoegen(gesorteerd, index, duurMin = STANDAARD_DUUR_MIN) {
  const lijst = gesorteerd || [];
  if (!lijst.length) return 9 * 3600;                         // lege planning: 09:00
  if (index <= 0) return Math.max(0, (lijst[0].fields?.Tijd || 0) - duurMin * 60);
  // Geen afkappen op 23:59: net als 'Herbereken tijden' mag een planning over middernacht lopen
  // (afkappen gaf overlap, Codex r1).
  return eindeVan(lijst[Math.min(index, lijst.length) - 1]);
}

/**
 * Nieuwe begintijd voor een versleepte regel die op positie `doelIndex` komt in de lijst zónder
 * die regel: einde van de regel erboven; bovenaan: eerste tijd − eigen duur (niet vóór 00:00).
 */
export function tijdNaVerslepen(gesorteerd, id, doelIndex) {
  const regel = (gesorteerd || []).find((p) => p.id === id);
  const rest = (gesorteerd || []).filter((p) => p.id !== id);
  if (!regel || !rest.length) return regel?.fields?.Tijd || 0;
  if (doelIndex <= 0) return Math.max(0, (rest[0].fields?.Tijd || 0) - (Number(regel.fields?.['Duur (minuten)']) || 0) * 60);
  return eindeVan(rest[Math.min(doelIndex, rest.length) - 1]);
}
